import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, Send, Loader2 } from "lucide-react";

const fmtDT = (s) => (s ? new Date(s).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");

export const ApprovalThread = ({ kind, itemId, thread = [], canReply, onPosted }) => {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const post = useMutation({
    mutationFn: async () => (await api.post(`/approvals/${kind}/${itemId}/comments`, { text: text.trim() })).data,
    onSuccess: (msg) => {
      setText("");
      onPosted?.(msg);
      qc.invalidateQueries({ queryKey: ["approvals-pending"] });
      qc.invalidateQueries({ queryKey: [kind === "quote" ? "quotes" : "orders"] });
      toast.success("Reply sent — the other side has been notified");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not send the reply"),
  });

  if (!thread.length && !canReply) return null;
  return (
    <div className="border border-slate-200 rounded-sm p-3 bg-white" data-testid={`approval-thread-${itemId}`}>
      <div className="stamp-label text-slate-500 mb-1.5 flex items-center gap-1.5">
        <MessageSquare size={11} /> Approval discussion{thread.length ? ` (${thread.length})` : ""} — internal only
      </div>
      <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
        {thread.length === 0 && (
          <div className="text-[11px] text-slate-400 italic" data-testid={`approval-thread-empty-${itemId}`}>
            No replies yet — ask a question or add context without pushing it back.
          </div>
        )}
        {thread.map((m) => (
          <div key={m.id} className="text-xs" data-testid={`approval-msg-${m.id}`}>
            <span className="font-semibold text-slate-800">{m.by_name || m.by}</span>
            <span className="text-[10px] text-slate-400 ml-1.5 font-mono">{fmtDT(m.at)}</span>
            <div className="text-slate-700 mt-0.5 whitespace-pre-wrap leading-snug">{m.text}</div>
          </div>
        ))}
      </div>
      {canReply && (
        <div className="flex gap-2 mt-2 items-end">
          <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000}
            placeholder="Reply — goes to the other side of the approval…"
            className="rounded-sm text-xs min-h-[36px]" data-testid={`approval-reply-input-${itemId}`} />
          <Button size="sm" disabled={post.isPending || !text.trim()}
            onClick={() => post.mutate()}
            className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white shrink-0 h-9 w-9 p-0"
            data-testid={`approval-reply-send-${itemId}`}>
            {post.isPending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          </Button>
        </div>
      )}
    </div>
  );
};
